import React from "react";

export default function AIChannelCenter() {

  const aiServices = [
    {
      icon: "💬",
      title: "AI Health Assistant",
      description: "Ask any general health questions"
    },
    {
      icon: "🧠",
      title: "Mental Wellness Chat",
      description: "Talk to our AI therapist anytime"
    },
    {
      icon: "📋",
      title: "Prescription Scanner",
      description: "Scan and understand medications"
    },
    {
      icon: "📄",
      title: "Lab Report Analyzer",
      description: "Get simple insights from your complex reports"
    },
    {
      icon: "🖼️",
      title: "Medical Image Analyzer",
      description: "Analyze X-Rays & MRI results"
    }
  ];

  return (
    <div id="ai-channel" className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 py-12 px-4 sm:px-6 lg:px-8">
      <style>{`
        .premium-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 24px;
          background: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(100, 116, 139, 0.2);
          border-radius: 50px;
          backdrop-filter: blur(10px);
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
          letter-spacing: 0.3px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          animation: slideDown 0.6s ease-out;
        }

        .premium-badge:hover {
          background: rgba(255, 255, 255, 0.95);
          border-color: rgba(15, 23, 42, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }

        .badge-dot {
          display: inline-block;
          width: 8px;
          height: 8px;
          background-color: #0d9488;
          border-radius: 50%;
          animation: badgePulse 2s ease-in-out infinite;
        }

        @keyframes badgePulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes iconFloat {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-12px) rotate(5deg);
          }
        }

        .service-icon {
          display: inline-block;
          animation: iconFloat 3s ease-in-out infinite;
          transition: all 0.3s ease;
        }

        .service-icon:hover {
          animation: none;
          transform: scale(1.2) rotate(10deg);
        }

        .premium-hero-heading {
          font-size: clamp(36px, 8vw, 72px);
          font-weight: 700;
          color: #0f172a;
          line-height: 1.1;
          letter-spacing: -1px;
          margin: 32px 0 20px 0;
          animation: slideDown 0.7s ease-out 0.1s both;
        }

        .premium-hero-subtitle {
          font-size: clamp(16px, 2.5vw, 22px);
          color: #475569;
          font-weight: 400;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
          letter-spacing: -0.2px;
          animation: slideDown 0.7s ease-out 0.2s both;
        }

        .premium-hero-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }
      `}</style>

      <div className="max-w-6xl mx-auto">
        {/* Premium Hero Section */}
        <div className="premium-hero-container">
          <button className="premium-badge">
            <span className="badge-dot"></span>
            AI Channel Center
          </button>
          <h1 className="premium-hero-heading">
            AI Channel Center
          </h1>
          <p className="premium-hero-subtitle">
            Experience the future of healthcare with our advanced AI-powered services
          </p>
        </div>

        {/* Pro Access Banner */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-teal-700 to-teal-600 rounded-3xl p-8 sm:p-12 text-white shadow-2xl overflow-hidden relative">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl -ml-36 -mb-36"></div>

            <div className="relative z-10">
              <div className="inline-block bg-yellow-400 text-teal-900 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
                ⭐ PRO ACCESS
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4">Unlock Every Service</h2>
              <p className="text-lg text-teal-100 mb-8 max-w-xl">
                Get unlimited doctor consults, premium AI analysis, and Priority Support.
              </p>
              <button className="bg-white text-teal-700 px-8 py-3 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 flex items-center gap-2 hover:gap-3">
                Try 7 days free
                <span>→</span>
              </button>
            </div>
          </div>
        </div>

        {/* AI Health Services Grid */}
        <div>
          <h3 className="text-2xl font-bold text-slate-900 mb-8">AI Health Services</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {aiServices.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group hover:scale-105 border border-slate-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="service-icon text-4xl">{service.icon}</div>
                  <div className="text-slate-300 group-hover:text-teal-500 transition-colors">
                    →
                  </div>
                </div>
                <h4 className="font-bold text-slate-900 mb-2 text-lg">
                  {service.title}
                </h4>
                <p className="text-sm text-slate-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-6">
            Ready to experience AI-powered healthcare?
          </p>
          <button className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-10 py-4 rounded-full font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105">
            Get Started Now
          </button>
        </div>
      </div>
    </div>
  );
}
